import { useMemo, useState } from "react";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  FileText,
  History,
  RotateCcw,
  Search,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { DashboardShell, PlaceholderCard } from "@/components/DashboardShell";
import { ADMIN_NAV } from "@/lib/nav";
import { useAuth } from "@/lib/auth";
import type { ServiceType } from "@/lib/services";
import {
  CHANGE_REASONS,
  REJECT_REASONS,
  REVIEW_SECTIONS,
  VERIFICATION_QUEUES,
  VERIFICATION_SERVICES,
  VERIFICATION_STATUS_META,
  decideVerification,
  fetchVerificationHistory,
  fetchVerifications,
  formatDateTime,
  kindLabel,
  signDocuments,
  type ReviewSectionKey,
  type VerificationRow,
} from "@/lib/verifications";

export const Route = createFileRoute("/_authenticated/admin/verifications/$status")({
  beforeLoad: ({ params }) => {
    if (!VERIFICATION_QUEUES.some((q) => q.slug === params.status)) throw notFound();
  },
  head: ({ params }) => {
    const q = VERIFICATION_QUEUES.find((x) => x.slug === params?.status);
    const title = `${q?.label ?? "Verifications"} · Landoo Admin`;
    return {
      meta: [
        { title },
        {
          name: "description",
          content:
            "Review partner identity, selfie, documents, agreement and service verification submissions.",
        },
        { property: "og:title", content: title },
        {
          property: "og:description",
          content:
            "Landoo admin verification dashboard for Sale, Rent, Lease and Parking partners.",
        },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary" },
      ],
    };
  },
  errorComponent: () => (
    <DashboardShell title="Verifications" role="Admin" items={ADMIN_NAV}>
      <PlaceholderCard
        title="Could not load verifications"
        description="Please refresh and try again."
      />
    </DashboardShell>
  ),
  notFoundComponent: () => (
    <DashboardShell title="Verifications" role="Admin" items={ADMIN_NAV}>
      <PlaceholderCard
        title="Unknown queue"
        description="Pick Pending, Approved, Rejected or Changes Requested."
      />
    </DashboardShell>
  ),
  component: AdminVerificationsPage,
});

type Decision = "approved" | "rejected" | "changes_requested";

function AdminVerificationsPage() {
  const { status: slug } = Route.useParams();
  const { user } = useAuth();
  const qc = useQueryClient();
  const queue = VERIFICATION_QUEUES.find((q) => q.slug === slug)!;

  const [service, setService] = useState<ServiceType | "all">("all");
  const [q, setQ] = useState("");
  const [active, setActive] = useState<VerificationRow | null>(null);

  const { data = [], isLoading } = useQuery({
    queryKey: ["admin-verifications", queue.status, service],
    queryFn: () => fetchVerifications(queue.status, service),
  });

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return data;
    return data.filter(
      (r) =>
        (r.partner?.full_name ?? "").toLowerCase().includes(term) ||
        (r.partner?.email ?? "").toLowerCase().includes(term) ||
        (r.partner?.phone ?? "").toLowerCase().includes(term) ||
        r.verification_kind.toLowerCase().includes(term),
    );
  }, [data, q]);

  return (
    <DashboardShell title={queue.label} role="Admin" items={ADMIN_NAV}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
          <ShieldCheck className="h-4 w-4" />
          {isLoading ? "Loading…" : `${rows.length} submission(s)`}
        </div>
        <div className="relative w-full sm:w-64">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search partner, email, mobile…"
            className="w-full rounded-full border border-border bg-card py-2 pl-9 pr-3 text-sm outline-none focus:border-coral"
          />
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {VERIFICATION_QUEUES.map((x) => (
          <Link
            key={x.slug}
            to="/admin/verifications/$status"
            params={{ status: x.slug }}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
              x.slug === slug ? "bg-coral text-white" : "bg-secondary text-muted-foreground"
            }`}
          >
            {x.label}
          </Link>
        ))}
      </div>

      <div className="mt-2 flex flex-wrap gap-2">
        {[{ value: "all", label: "All services" }, ...VERIFICATION_SERVICES].map((s) => (
          <button
            key={s.value}
            onClick={() => setService(s.value as ServiceType | "all")}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
              service === s.value
                ? "border-coral text-coral"
                : "border-border text-muted-foreground"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <div className="mt-4">
          <PlaceholderCard
            title={isLoading ? "Loading…" : `No ${queue.label.toLowerCase()} submissions`}
            description="Partner verification submissions matching this queue and service filter will appear here."
          />
        </div>
      ) : (
        <div className="mt-4 grid gap-3 lg:grid-cols-2">
          {rows.map((r) => (
            <button
              key={r.id}
              onClick={() => setActive(r)}
              className="rounded-2xl border border-border bg-card p-4 text-left shadow-soft transition hover:border-coral"
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-semibold text-brown-dark">
                    {r.partner?.full_name ?? "Partner"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {r.partner?.email ?? r.user_id.slice(0, 8)}
                  </p>
                </div>
                <span
                  className={`rounded-full px-2 py-1 text-[10px] font-semibold ${
                    VERIFICATION_STATUS_META[queue.status].cls
                  }`}
                >
                  {VERIFICATION_STATUS_META[queue.status].label}
                </span>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
                <span className="rounded-full bg-secondary px-2 py-1 font-semibold uppercase tracking-wide text-muted-foreground">
                  {r.service_type ?? "general"}
                </span>
                <span className="text-muted-foreground">{kindLabel(r.verification_kind)}</span>
                <span className="text-muted-foreground">· {formatDateTime(r.created_at)}</span>
              </div>
              {r.reviewer_notes && (
                <p className="mt-2 line-clamp-2 text-xs text-muted-foreground">
                  {r.reviewer_notes}
                </p>
              )}
            </button>
          ))}
        </div>
      )}

      {active && (
        <ReviewDrawer
          row={active}
          reviewerId={user?.id ?? ""}
          canDecide={queue.status !== "approved"}
          onClose={() => setActive(null)}
          onDone={() => {
            setActive(null);
            qc.invalidateQueries({ queryKey: ["admin-verifications"] });
          }}
        />
      )}
    </DashboardShell>
  );
}

function ReviewDrawer({
  row,
  reviewerId,
  canDecide,
  onClose,
  onDone,
}: {
  row: VerificationRow;
  reviewerId: string;
  canDecide: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [checked, setChecked] = useState<ReviewSectionKey[]>([]);
  const [decision, setDecision] = useState<Decision | null>(null);
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");

  const { data: docs = [] } = useQuery({
    queryKey: ["verification-docs", row.id],
    queryFn: () => signDocuments(row.document_paths ?? []),
    enabled: (row.document_paths?.length ?? 0) > 0,
  });

  const { data: history = [] } = useQuery({
    queryKey: ["verification-history", row.id],
    queryFn: () => fetchVerificationHistory(row.id),
  });

  const submit = useMutation({
    mutationFn: () =>
      decideVerification({
        verification: row,
        reviewerId,
        decision: decision!,
        reason: reason || undefined,
        notes: notes || undefined,
        sections: checked,
      }),
    onSuccess: () => {
      toast.success(
        decision === "approved"
          ? "Verification approved — partner notified"
          : decision === "rejected"
            ? "Verification rejected — partner notified"
            : "Changes requested — partner notified",
      );
      onDone();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const needsReason = decision === "rejected" || decision === "changes_requested";
  const reasonList = decision === "rejected" ? REJECT_REASONS : CHANGE_REASONS;
  const data = (row.submitted_data ?? {}) as Record<string, unknown>;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <div
        className="h-full w-full max-w-lg overflow-y-auto bg-card p-5 shadow-xl sm:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-brown-dark">
              {row.partner?.full_name ?? "Partner"} · {kindLabel(row.verification_kind)}
            </h2>
            <p className="text-xs text-muted-foreground">
              {(row.service_type ?? "general").toUpperCase()} · submitted{" "}
              {formatDateTime(row.created_at)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-full border border-border px-3 py-1 text-xs font-semibold"
          >
            Close
          </button>
        </div>

        <section className="mt-4 rounded-2xl border border-border p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Review checklist
          </p>
          <div className="mt-2 grid gap-2">
            {REVIEW_SECTIONS.map((s) => (
              <label key={s.key} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={checked.includes(s.key)}
                  onChange={(e) =>
                    setChecked((prev) =>
                      e.target.checked ? [...prev, s.key] : prev.filter((k) => k !== s.key),
                    )
                  }
                  className="h-4 w-4 accent-[hsl(var(--coral,12_80%_60%))]"
                />
                <span className="font-medium text-brown-dark">{s.label}</span>
                <span className="text-xs text-muted-foreground">{sectionHint(s.key, data)}</span>
              </label>
            ))}
          </div>
        </section>

        <section className="mt-4 rounded-2xl border border-border p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Submitted details
          </p>
          {Object.keys(data).length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">No structured data submitted.</p>
          ) : (
            <dl className="mt-2 grid gap-1 text-sm">
              {Object.entries(data).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3">
                  <dt className="text-muted-foreground">{kindLabel(k)}</dt>
                  <dd className="text-right font-medium text-brown-dark">{String(v)}</dd>
                </div>
              ))}
            </dl>
          )}
        </section>

        <section className="mt-4 rounded-2xl border border-border p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Documents
          </p>
          {docs.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">No documents attached.</p>
          ) : (
            <ul className="mt-2 grid gap-2">
              {docs.map((d) => (
                <li key={d.path}>
                  {d.url ? (
                    <a
                      href={d.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-coral hover:underline"
                    >
                      <FileText className="h-4 w-4" /> {d.path.split("/").pop()}
                    </a>
                  ) : (
                    <span className="text-sm text-muted-foreground">{d.path} (unavailable)</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        {canDecide && (
          <section className="mt-4 rounded-2xl border border-border p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Decision
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <DecisionButton
                current={decision}
                value="approved"
                onSelect={setDecision}
                icon={CheckCircle2}
                label="Approve"
              />
              <DecisionButton
                current={decision}
                value="rejected"
                onSelect={setDecision}
                icon={XCircle}
                label="Reject"
              />
              <DecisionButton
                current={decision}
                value="changes_requested"
                onSelect={setDecision}
                icon={RotateCcw}
                label="Request Changes"
              />
            </div>

            {needsReason && (
              <div className="mt-3">
                <select
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full rounded-xl border border-border bg-card px-3 py-2 text-sm"
                >
                  <option value="">Select a reason…</option>
                  {reasonList.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Notes for the partner (optional)"
              className="mt-3 w-full rounded-xl border border-border bg-card px-3 py-2 text-sm"
            />

            <button
              disabled={!decision || (needsReason && !reason) || submit.isPending}
              onClick={() => submit.mutate()}
              className="mt-3 w-full rounded-full bg-coral px-4 py-2.5 text-sm font-semibold text-white shadow-soft disabled:opacity-50"
            >
              {submit.isPending ? "Saving…" : "Submit decision & notify partner"}
            </button>
          </section>
        )}

        <section className="mt-4 rounded-2xl border border-border p-4">
          <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <History className="h-3.5 w-3.5" /> Verification history (read-only)
          </p>
          {history.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">No decisions recorded yet.</p>
          ) : (
            <ol className="mt-3 space-y-3">
              {history.map((h) => (
                <li key={h.id} className="border-l-2 border-border pl-3">
                  <p className="text-sm font-semibold text-brown-dark">
                    {VERIFICATION_STATUS_META[h.decision as keyof typeof VERIFICATION_STATUS_META]
                      ?.label ?? h.decision}
                  </p>
                  <p className="text-xs text-muted-foreground">{formatDateTime(h.created_at)}</p>
                  {h.reason && (
                    <p className="mt-1 text-xs text-muted-foreground">Reason: {h.reason}</p>
                  )}
                  {h.notes && <p className="text-xs text-muted-foreground">Notes: {h.notes}</p>}
                  {h.reviewed_sections && h.reviewed_sections.length > 0 && (
                    <p className="text-[11px] text-muted-foreground">
                      Reviewed: {h.reviewed_sections.map((s) => kindLabel(s)).join(", ")}
                    </p>
                  )}
                </li>
              ))}
            </ol>
          )}
          <p className="mt-3 text-[11px] text-muted-foreground">
            History entries are permanent and cannot be edited or deleted.
          </p>
        </section>
      </div>
    </div>
  );
}

function DecisionButton({
  current,
  value,
  onSelect,
  icon: Icon,
  label,
}: {
  current: Decision | null;
  value: Decision;
  onSelect: (d: Decision) => void;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}) {
  const active = current === value;
  return (
    <button
      onClick={() => onSelect(value)}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-2 text-xs font-semibold transition ${
        active ? "border-coral bg-coral/10 text-coral" : "border-border text-muted-foreground"
      }`}
    >
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
}

function sectionHint(key: string, data: Record<string, unknown>) {
  const v =
    key === "identity"
      ? (data.id_number ?? data.identity ?? data.document_type)
      : key === "selfie"
        ? (data.selfie ?? data.live_video)
        : key === "agreement"
          ? (data.agreement_id ?? data.agreement_status)
          : key === "service"
            ? (data.service_type ?? data.business_name)
            : undefined;
  return v ? `— ${String(v)}` : "";
}
